import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { getCoreAxiosInstance } from "../../api/CoreAxiosInstance";
import { PacketResultSchema, PacketFacetsSchema, type SeverityTier } from "../types";

interface PacketListParams {
  batchId: string;
  orgCategory: string;
  orgId: string;
  filters?: {
    severityTiers?: SeverityTier[];
    component?: string;
    payGroups?: string[];
    payPeriodEnds?: string[];
    page?: number;
    pageSize?: number;
  };
}

const PacketListResponseSchema = z.object({
  items: z.array(PacketResultSchema),
  total: z.number(),
  page: z.number(),
  page_size: z.number(),
  total_pages: z.number(),
  facets: PacketFacetsSchema.optional(),
});

export type PacketListResponse = z.infer<typeof PacketListResponseSchema>;

export function useShadowRunPackets({
  batchId,
  orgCategory,
  orgId,
  filters,
}: PacketListParams) {
  return useQuery({
    queryKey: [
      "shadowRunPackets",
      orgCategory,
      orgId,
      batchId,
      filters,
    ],
    queryFn: async () => {
      const instance = await getCoreAxiosInstance();
      const { data } = await instance.get(
        `/api/v1/shadow/batches/${orgCategory}/${orgId}/${batchId}/packets`,
        {
          params: {
            ...(filters?.severityTiers?.length
              ? { severity_tiers: filters.severityTiers.join(",") }
              : {}),
            ...(filters?.component ? { component: filters.component } : {}),
            ...(filters?.payGroups?.length
              ? { pay_group: filters.payGroups }
              : {}),
            ...(filters?.payPeriodEnds?.length
              ? { pay_period_end: filters.payPeriodEnds }
              : {}),
            ...(filters?.page !== undefined ? { page: filters.page } : {}),
            ...(filters?.pageSize !== undefined
              ? { page_size: filters.pageSize }
              : {}),
          },
          paramsSerializer: {
            indexes: null,
          },
        }
      );
      return PacketListResponseSchema.parse(data);
    },
    enabled: !!batchId && !!orgCategory && !!orgId,
  });
}
